import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, RotateCcw } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];
const shuffle = (arr) => { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };

// Juego de Memorama. props: config {pairs:[{a,b}]}, onComplete({moves, seconds})
export default function MemoramaGame({ config, onComplete, busy }) {
  const pairs = config?.pairs || [];
  const [deck, setDeck] = useState([]);
  const [flipped, setFlipped] = useState([]);   // indices currently face-up (not matched)
  const [matched, setMatched] = useState([]);   // pairKeys matched
  const [moves, setMoves] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [locked, setLocked] = useState(false);
  const done = useRef(false);
  const timer = useRef(null);

  const build = () => {
    const cards = [];
    pairs.forEach((p, k) => {
      cards.push({ id: `${k}-a`, key: k, text: p.a, side: "a" });
      cards.push({ id: `${k}-b`, key: k, text: p.b, side: "b" });
    });
    setDeck(shuffle(cards)); setFlipped([]); setMatched([]); setMoves(0); setSeconds(0); done.current = false;
  };
  useEffect(build, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer.current);
  }, []);

  useEffect(() => {
    if (pairs.length && matched.length === pairs.length && !done.current) {
      done.current = true; clearInterval(timer.current);
      setTimeout(() => onComplete({ moves, seconds }), 600);
    }
  }, [matched]); // eslint-disable-line react-hooks/exhaustive-deps

  const clickCard = (idx) => {
    if (locked || busy) return;
    const card = deck[idx];
    if (matched.includes(card.key) || flipped.includes(idx)) return;
    const nf = [...flipped, idx];
    setFlipped(nf);
    if (nf.length === 2) {
      setMoves((m) => m + 1);
      const [a, b] = nf;
      if (deck[a].key === deck[b].key) { setMatched((m) => [...m, deck[a].key]); setFlipped([]); }
      else { setLocked(true); setTimeout(() => { setFlipped([]); setLocked(false); }, 850); }
    }
  };

  if (!pairs.length) return <p className="text-navy/40 text-sm">Este memorama no tiene pares.</p>;
  const cols = deck.length <= 8 ? 4 : deck.length <= 12 ? 4 : deck.length <= 16 ? 4 : 6;

  return (
    <div data-testid="memorama-game">
      <div className="flex items-center justify-between mb-4 text-sm">
        <span className="text-navy/50">Movimientos: <b className="text-navy" data-testid="memorama-moves">{moves}</b></span>
        <span className="text-navy/50">Pares: <b className="text-navy">{matched.length}/{pairs.length}</b></span>
        <span className="text-navy/50 tabular-nums">{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}</span>
      </div>
      <div className="grid gap-2.5" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }} data-testid="memorama-grid">
        {deck.map((card, idx) => {
          const isUp = flipped.includes(idx) || matched.includes(card.key);
          const isMatched = matched.includes(card.key);
          return (
            <button key={card.id} data-testid={`memo-card-${idx}`} onClick={() => clickCard(idx)}
              className="relative aspect-[3/4] [perspective:800px]" aria-label="carta">
              <motion.div className="absolute inset-0 [transform-style:preserve-3d] transition-transform duration-300"
                animate={{ rotateY: isUp ? 180 : 0 }}>
                {/* back */}
                <div className="absolute inset-0 rounded-xl bg-navy flex items-center justify-center [backface-visibility:hidden]">
                  <Sparkles className="w-5 h-5 text-gold/70" />
                </div>
                {/* front */}
                <div className={`absolute inset-0 rounded-xl border-2 p-2 flex items-center justify-center text-center [backface-visibility:hidden] [transform:rotateY(180deg)] ${isMatched ? "border-emerald-400 bg-emerald-50" : "border-violet/40 bg-white"}`}>
                  <span className="text-[11px] sm:text-xs font-medium text-navy leading-tight line-clamp-4">{card.text}</span>
                </div>
              </motion.div>
            </button>
          );
        })}
      </div>
      <button data-testid="memorama-restart" onClick={build} className="mt-4 text-xs text-navy/50 hover:text-violet inline-flex items-center gap-1.5">
        <RotateCcw className="w-3.5 h-3.5" /> Reiniciar
      </button>
    </div>
  );
}
